import { formatLite } from './engine-lite.js';

let worker;
let nextRequestId = 0;
const pending = new Map();
let graphInFlight = false;
let queuedGraph = null;

function createAbortError() {
  const error = new Error('Calcul graphique remplacé');
  error.name = 'AbortError';
  return error;
}

function fallback(type, payload) {
  if (type === 'complex') {
    return import(
      /* webpackChunkName: 'complex-mathjs' */ './complexEngine.js'
    ).then(({ evaluateComplex }) =>
      evaluateComplex(payload.input, payload.scope, payload.options)
    );
  }

  if (type === 'graph') {
    return import('./graphMath.js').then(({ calculateGraph }) =>
      calculateGraph(payload)
    );
  }

  return import('./engine.js').then((engine) => {
    if (type === 'equation') {
      return engine.solveEquation(
        payload.input,
        payload.variable,
        payload.options
      );
    }
    if (type === 'derivative') {
      return engine.differentiate(
        payload.input,
        payload.variable,
        payload.point,
        payload.options
      );
    }
    if (type === 'integral') {
      return engine.integrate(
        payload.input,
        payload.variable,
        payload.start,
        payload.end,
        payload.options
      );
    }
    if (type === 'matrix') {
      return engine.matrixOperation(
        payload.input,
        payload.operation,
        payload.options
      );
    }
    throw new Error('Operation scientifique inconnue');
  });
}

function rejectOrFallback(error) {
  const requests = [...pending.entries()];
  pending.clear();
  worker?.terminate();
  worker = undefined;
  requests.forEach(([, request]) => {
    fallback(request.type, request.payload)
      .then(request.resolve)
      .catch(() => request.reject(error));
  });
}

function getWorker() {
  if (worker) return worker;
  if (typeof Worker === 'undefined') return null;

  try {
    worker = new Worker(
      new URL('../workers/scientific.worker.js', import.meta.url)
    );
    worker.onmessage = ({ data }) => {
      const request = pending.get(data.id);
      if (!request) return;
      pending.delete(data.id);
      if (data.error) request.reject(new Error(data.error));
      else request.resolve(data.result);
    };
    worker.onerror = (error) => rejectOrFallback(error);
    return worker;
  } catch {
    worker = undefined;
    return null;
  }
}

export function runScientificTask(type, payload) {
  const currentWorker = getWorker();
  if (!currentWorker) return fallback(type, payload);

  return new Promise((resolve, reject) => {
    const id = ++nextRequestId;
    pending.set(id, { type, payload, resolve, reject });
    try {
      currentWorker.postMessage({ id, type, payload });
    } catch (error) {
      pending.delete(id);
      fallback(type, payload)
        .then(resolve)
        .catch(() => reject(error));
    }
  });
}

function pumpGraphQueue() {
  if (graphInFlight || !queuedGraph) return;
  const request = queuedGraph;
  queuedGraph = null;
  graphInFlight = true;
  runScientificTask('graph', request.payload)
    .then(request.resolve, request.reject)
    .finally(() => {
      graphInFlight = false;
      pumpGraphQueue();
    });
}

export function runLatestScientificTask(type, payload) {
  if (type !== 'graph') return runScientificTask(type, payload);
  return new Promise((resolve, reject) => {
    if (queuedGraph) queuedGraph.reject(createAbortError());
    queuedGraph = { payload, resolve, reject };
    pumpGraphQueue();
  });
}
export { formatLite };
